import type { ColumnDef } from '@tanstack/react-table';

import { Attribute } from '@/common/attribute';
import { Can } from '@/components/can/Can';
import CrumbTitle from '@/components/crumbTitle/CrumbTitle';
import { DataTableColumnHeader } from '@/components/dataTable/ColumnHeader';
import { getSerialNumber } from '@/components/dataTable/data-table.utils';
import { PERMS } from '@/features/auth/permissions/permissionKeys';
import { SupportedLocale, Translation } from '@/hoslog-intl/Translation';
import { CheckBoxIcon } from '@/icons';
import { formatDate } from '@/lib/dateFormatter';
import {
  CLUB_VERIFICATION_STATUS,
  PAYMENT_STATUS,
  ReservationLedger,
} from '@/services/dashboard/reservationLedger/reservationLedger.types';
import { Link } from '@tanstack/react-router';
import { ReactNode } from 'react';
import { getClubVerificationStatusLabel, getPaymentStatusLabel, getPlanTypeLabel } from '../utils/statusLabels';
import { VisitCodeConfirmation } from '../VisitCodeConfirmation';

type ModalSize = 'sm' | 'md' | 'lg';

type OnOpenProps = <T extends string>(config: {
  title: T;
  modalTitle: string | null;
  okText: string | ReactNode;
  component: ReactNode;
  cancelText?: string | ReactNode;
  size?: ModalSize;
  onCancel?: () => void;
}) => void;
enum reservationTypeOption {
  VIA_HOSLOG = 'ホスログ経由',
  STORE_DIRECT = '店直',
}
export function getColumns(
  onOpen: OnOpenProps,
  t: (key: string) => string,
  reservationPlanTypes: Attribute[],
  locale: SupportedLocale,
): ColumnDef<ReservationLedger>[] {
  return [
    {
      accessorKey: 'sn',
      header: ({ column }) => <DataTableColumnHeader column={column} title={t('common.page.tableColumn.sn')} />,
      cell: ({ row, table }) => (
        <div className="font-medium text-caption text-foreground truncate max-w-[10rem]">
          {getSerialNumber(table, row.index)}
        </div>
      ),
      enableSorting: false,
      enableHiding: false,
    },
    {
      accessorKey: 'invoiceNumber',
      header: ({ column }) => <DataTableColumnHeader column={column} title={t('common.page.tableColumn.resId')} />,
      cell: ({ row }) => (
        <div className="font-medium text-caption text-foreground truncate max-w-[20rem]">
          {row.getValue('invoiceNumber')}
        </div>
      ),
      meta: {
        hideColumnLabelKey: 'common.page.tableColumn.resId',
      },
    },
    {
      accessorKey: 'nickName',
      header: ({ column }) => (
        <DataTableColumnHeader
          className="whitespace-nowrap"
          column={column}
          title={t('common.page.tableColumn.nickName')}
        />
      ),
      cell: ({ row }) => (
        <div className="font-medium text-caption text-foreground truncate max-w-[20rem]">
          {row.getValue('nickName')}
        </div>
      ),
      meta: {
        hideColumnLabelKey: 'common.page.tableColumn.nickName',
      },
    },

    {
      id: 'storeInfo',
      accessorKey: 'storeInfo',
      header: ({ column }) => <DataTableColumnHeader column={column} title={t('common.page.tableColumn.storeInfo')} />,
      cell: ({ row }) => {
        const clubImage = row.original.clubImage;
        const storeInfoEng = row.original.storeInfoEng;
        const storeInfoJP = row.original.storeInfoJpn;
        return (
          <div className="flex items-center space-x-3 min-w-[210px]">
            {clubImage && (
              <img
                src={clubImage.path}
                alt={clubImage.description}
                className="w-8 h-8 rounded-full object-cover"
                onError={(e) => {
                  const target = e.target as HTMLImageElement;
                  target.style.display = 'none';
                }}
              />
            )}
            {row.original.clubStatus === 'A' ? (
              <div>
                <Link to="/store-information/premium-stores" search={{ q: storeInfoEng }} className="cursor-pointer">
                  <div className="font-medium text-caption text-link underline">{storeInfoEng}</div>
                </Link>
                {storeInfoJP && (
                  <div className="text-foreground-subtle font-medium text-xs break-all">{storeInfoJP}</div>
                )}
              </div>
            ) : (
              <div>
                <div className="font-medium text-caption text-link underline hover:cursor-not-allowed">
                  {storeInfoEng}
                </div>
                {storeInfoJP && (
                  <div className="text-foreground-subtle font-medium text-xs break-all">{storeInfoJP}</div>
                )}{' '}
              </div>
            )}
          </div>
        );
      },
      meta: {
        hideColumnLabelKey: 'common.page.tableColumn.storeInfo',
      },
      enableSorting: true,
    },
    {
      id: 'createdDate',
      accessorKey: 'reservationDate',
      header: ({ column }) => (
        <DataTableColumnHeader
          className="whitespace-nowrap"
          column={column}
          title={t('common.page.tableColumn.reservartionDate')}
        />
      ),
      cell: ({ row }) => {
        const visitingDateTime = row.original.visitingDateTime;
        const endingDateTime = row.original.endingDateTime;
        const date = formatDate(visitingDateTime, 'YYYY/MM/DD HH:mm').split(' ')[0];
        const visitingTime = formatDate(visitingDateTime, 'YYYY/MM/DD HH:mm').split(' ')[1];
        const endingTime = formatDate(endingDateTime, 'YYYY/MM/DD HH:mm').split(' ')[1];
        return (
          <div className="font-medium truncate text-caption text-foreground max-w-[10rem]">{`${date} ${visitingTime}~${endingTime}`}</div>
        );
      },
      meta: {
        hideColumnLabelKey: 'common.page.tableColumn.reservartionDate',
      },
      enableColumnFilter: true,
    },
    {
      accessorKey: 'reservationType',
      header: ({ column }) => (
        <DataTableColumnHeader
          className="whitespace-nowrap"
          column={column}
          title={t('common.page.tableColumn.reservationType')}
        />
      ),
      cell: ({ row }) => {
        const reservationType = row.getValue('reservationType');
        return (
          <div className="text-caption font-medium text-foreground max-w-[10rem]">
            {reservationType === reservationTypeOption.VIA_HOSLOG && (
              <Translation id={'dashboard.reservationLedger.table.column.viaHoslog'} />
            )}
            {reservationType === reservationTypeOption.STORE_DIRECT && (
              <Translation id={'dashboard.reservationLedger.table.column.storeDirect'} />
            )}
          </div>
        );
      },
      meta: {
        hideColumnLabelKey: 'common.page.tableColumn.reservationType',
      },
    },
    {
      accessorKey: 'planType',
      header: ({ column }) => (
        <DataTableColumnHeader
          column={column}
          title={t('common.page.tableColumn.planType')}
          className="whitespace-nowrap"
        />
      ),
      cell: ({ row }) => {
        const planTypes = row.getValue('planType');
        const label = getPlanTypeLabel(planTypes as string, reservationPlanTypes, locale);
        return <div className="font-medium truncate max-w-[10rem] text-caption text-foreground">{label}</div>;
      },
      meta: {
        hideColumnLabelKey: 'dashboard.reservationLedger.table.filter.planType',
        label: t('dashboard.reservationLedger.table.filter.planType'),
        options: reservationPlanTypes.map((val) => {
          return {
            value: val.value,
            label: locale === 'en' ? val.labelEng : val.label,
          };
        }),
      },
    },
    {
      accessorKey: 'planName',
      header: ({ column }) => (
        <DataTableColumnHeader
          column={column}
          title={t('common.page.tableColumn.planName')}
          className="whitespace-nowrap"
        />
      ),
      cell: ({ row }) => (
        <div className="text-caption text-foreground font-medium truncate max-w-[10rem]">
          {row.getValue('planName')}
        </div>
      ),
      meta: {
        hideColumnLabelKey: 'common.page.tableColumn.planName',
      },
    },
    {
      accessorKey: 'numberOfPeople',
      header: ({ column }) => (
        <DataTableColumnHeader
          className="whitespace-nowrap"
          column={column}
          title={t('common.page.tableColumn.numberOfPeople')}
        />
      ),
      cell: ({ row }) => (
        <div className="text-caption text-foreground font-medium truncate max-w-[10rem]">
          {row.getValue('numberOfPeople')}名
        </div>
      ),
      meta: {
        hideColumnLabelKey: 'common.page.tableColumn.numberOfPeople',
      },
    },
    {
      accessorKey: 'clubVerificationStatus',
      header: ({ column }) => (
        <DataTableColumnHeader
          className="whitespace-nowrap"
          column={column}
          title={t('common.page.tableColumn.clubVerificationStatus')}
        />
      ),
      cell: ({ cell }) => {
        const status = cell.getValue<ReservationLedger['clubVerificationStatus']>();
        return (
          <div>
            {status === CLUB_VERIFICATION_STATUS.PENDING ? (
              <div className="text-warning text-caption font-medium">{getClubVerificationStatusLabel(status, t)}</div>
            ) : status === CLUB_VERIFICATION_STATUS.APPROVED ? (
              <div className="text-success text-caption font-medium">{getClubVerificationStatusLabel(status, t)}</div>
            ) : (
              <div className="text-destructive-text text-caption font-medium">
                {getClubVerificationStatusLabel(status, t)}
              </div>
            )}
          </div>
        );
      },
      meta: {
        hideColumnLabelKey: 'dashboard.reservationLedger.table.filter.status',
        label: t('dashboard.reservationLedger.table.filter.status'),
        options: [
          {
            label: t('common.page.table.approved'),
            value: CLUB_VERIFICATION_STATUS.APPROVED,
          },
          {
            label: t('common.page.table.pending'),
            value: CLUB_VERIFICATION_STATUS.PENDING,
          },
          {
            label: t('common.page.table.rejected'),
            value: CLUB_VERIFICATION_STATUS.REJECTED,
          },
        ],
      },
    },
    {
      accessorKey: 'paymentMethod',
      header: ({ column }) => (
        <DataTableColumnHeader
          className="whitespace-nowrap"
          column={column}
          title={t('common.page.tableColumn.paymentMethod')}
        />
      ),
      cell: ({ row }) => {
        const paymentMethod = row.getValue('paymentMethod');
        return (
          <div className="font-medium text-caption text-foreground truncate max-w-[10rem]">
            {paymentMethod === 'Hoslog Points' ? (
              <Translation id={'common.page.table.hoslogPoints'} />
            ) : paymentMethod === 'On site payment' ? (
              <Translation id={'common.page.table.onSitePayment'} />
            ) : (
              <div>-</div>
            )}
          </div>
        );
      },
      meta: {
        hideColumnLabelKey: 'common.page.tableColumn.paymentMethod',
      },
    },
    {
      accessorKey: 'paymentStatus',
      header: ({ column }) => (
        <DataTableColumnHeader
          className="whitespace-nowrap"
          column={column}
          title={t('common.page.tableColumn.paymentStatus')}
        />
      ),
      cell: ({ cell }) => {
        const status = cell.getValue<ReservationLedger['paymentStatus']>();
        return (
          <div>
            {status === PAYMENT_STATUS.PENDING ? (
              <div className="text-warning text-caption font-medium">{getPaymentStatusLabel(status, t)}</div>
            ) : status === PAYMENT_STATUS.SUCCESS ? (
              <div className="text-success text-caption font-medium">{getPaymentStatusLabel(status, t)}</div>
            ) : (
              <div className="text-destructive-text text-caption font-medium">{getPaymentStatusLabel(status, t)}</div>
            )}
          </div>
        );
      },
      meta: {
        hideColumnLabelKey: 'common.page.tableColumn.paymentStatus',
      },
    },
    {
      accessorKey: 'totalPlanAmount',
      header: ({ column }) => (
        <DataTableColumnHeader
          className="whitespace-nowrap"
          column={column}
          title={t('common.page.tableColumn.totalPlanAmount')}
        />
      ),
      cell: ({ row }) => (
        <div className="text-caption text-right text-foreground truncate font-medium max-w-[10rem] pr-3">
          {Number(row.getValue('totalPlanAmount')).toLocaleString()} 円
        </div>
      ),
      meta: { align: 'right', hideColumnLabelKey: 'common.page.tableColumn.totalPlanAmount' },
    },
    {
      accessorKey: 'totalPlanCommissionAmount',
      header: ({ column }) => (
        <DataTableColumnHeader
          className="whitespace-nowrap"
          column={column}
          title={t('common.page.tableColumn.totalPlanCommissionAmount')}
        />
      ),
      cell: ({ row }) => (
        <div className="text-caption text-right text-foreground truncate font-medium pr-3">
          {Number(row.getValue('totalPlanCommissionAmount')).toLocaleString()} 円
        </div>
      ),
      meta: { align: 'right', hideColumnLabelKey: 'common.page.tableColumn.totalPlanCommissionAmount' },
    },
    {
      accessorKey: 'totalClubAmount',
      header: ({ column }) => (
        <DataTableColumnHeader
          className="whitespace-nowrap"
          column={column}
          title={t('common.page.tableColumn.totalClubAmount')}
        />
      ),
      cell: ({ row }) => (
        <div className="text-caption text-right pr-3 text-foreground truncate font-medium max-w-[10rem]">
          {Number(row.getValue('totalClubAmount')).toLocaleString()} 円
        </div>
      ),
      meta: { align: 'right', hideColumnLabelKey: 'common.page.tableColumn.totalClubAmount' },
    },
    {
      accessorKey: 'totalAdminCommissionAmount',
      header: ({ column }) => (
        <DataTableColumnHeader
          className="whitespace-nowrap"
          column={column}
          title={t('common.page.tableColumn.totalAdminCommissionAmount')}
        />
      ),
      cell: ({ row }) => (
        <div className="text-caption pr-3 text-right text-foreground truncate font-medium">
          {Number(row.getValue('totalAdminCommissionAmount')).toLocaleString()} 円
        </div>
      ),
      meta: { align: 'right', hideColumnLabelKey: 'common.page.tableColumn.totalAdminCommissionAmount' },
    },
    {
      accessorKey: 'totalAdminAmount',
      header: ({ column }) => (
        <DataTableColumnHeader
          className="whitespace-nowrap"
          column={column}
          title={t('common.page.tableColumn.totalAdminAmount')}
        />
      ),
      cell: ({ row }) => (
        <div className="text-caption pr-3 text-right text-foreground truncate font-medium">
          {Number(row.getValue('totalAdminAmount')).toLocaleString()} 円
        </div>
      ),
      meta: { align: 'right', hideColumnLabelKey: 'common.page.tableColumn.totalAdminAmount' },
    },
    {
      id: 'actions',
      header: ({ column }) => (
        <DataTableColumnHeader
          className="whitespace-nowrap"
          column={column}
          title={t('common.page.tableColumn.actions')}
        />
      ),
      cell: ({ row }) => {
        const status = row.getValue('clubVerificationStatus');
        const paymentStatus = row.getValue<ReservationLedger['paymentStatus']>('paymentStatus');
        const reservationId = row.original.reservationId;
        const clubId = row.original.clubId;
        return (
          <Can rule={{ anyOf: [PERMS.reservation.otp.verify] }}>
            <div className="text-caption pr-3 text-right text-foreground truncate font-medium">
              {status === CLUB_VERIFICATION_STATUS.PENDING && paymentStatus === PAYMENT_STATUS.PENDING && (
                <CheckBoxIcon
                  height={16}
                  width={16}
                  color="var(--success)"
                  className="cursor-pointer"
                  onClick={() =>
                    onOpen({
                      title: 'Verify OTP',
                      okText: <CrumbTitle id="common.page.modal.confirm" />,
                      cancelText: <CrumbTitle id="common.page.modal.no" />,
                      size: 'lg',
                      modalTitle: 'dashboard.reservationLedger.modal.title',
                      component: <VisitCodeConfirmation reservationId={reservationId} clubId={clubId} />,
                    })
                  }
                />
              )}
            </div>
          </Can>
        );
      },
      size: 40,
    },
  ];
}
